import { createSlice } from '@reduxjs/toolkit';

// ── Helper: find a stop inside the nested route structure ─────────────────────
// API shape: state.stops = [{ id, assignedName, stops: [{id, status, ...}] }]
function findStop(state, stopId) {
  return state.stops?.[0]?.stops?.find((s) => s.id === stopId) ?? null;
}

const initialState = {
  stops: [],                    // [ { id, assignedName, stops: [...] } ]
  sortMode: 'route',            // 'route' | 'time' | 'priority'
  selectedStopId: null,
};

const routeSlice = createSlice({
  name: 'route',
  initialState,
  reducers: {
    setDriverStops(state, action) {
      state.stops = action.payload;
    },

    setSortMode(state, action) {
      state.sortMode = action.payload;
    },

    // Sets selectedStopId — App.js renders <DriverDetail /> when this is truthy
    selectStop(state, action) {
      state.selectedStopId = action.payload;
    },

    clearSelectedStop(state) {
      state.selectedStopId = null;
    },

    // status -> 'picked_up' (string, matches API + STATUS_STRING_TO_NUMERIC)
    pickupStop(state, action) {
      const stop = findStop(state, action.payload);
      if (stop) stop.status = 'picked_up';
    },

    // status -> 'delivered'
    deliverStop(state, action) {
      const stop = findStop(state, action.payload);
      if (stop) stop.status = 'delivered';
    },

    // Push a photo object into stop.photos[]
    // payload: { stopId, photo: { id, url, fileKey, photoType, label, previewUrl, timestamp } }
    addDriverPhoto(state, action) {
      const { stopId, photo } = action.payload;
      const stop = findStop(state, stopId);
      if (stop) {
        if (!stop.photos) stop.photos = [];
        stop.photos.push(photo);
      }
    },

    // Update arbitrary fields on a stop (e.g. after status API confirms)
    // payload: { stopId, updates: { status, timeline, ... } }
    updateDriverStop(state, action) {
      const { stopId, updates } = action.payload;
      const stop = findStop(state, stopId);
      if (stop) Object.assign(stop, updates);
    },

     mergeDriverStops(state, action) {
      const incoming = action.payload || [];
      if (!incoming.length) return;

      if (!state.stops[0]) {
        state.stops = [{ id: 'merged', assignedName: '', stops: [] }];
      }
      const bucket = state.stops[0];

      incoming.forEach((newStop) => {
        const idx = bucket.stops.findIndex((s) => s.id === newStop.id);
        if (idx >= 0) {
          bucket.stops[idx] = { ...bucket.stops[idx], ...newStop };
        } else {
          bucket.stops.push(newStop);
        }
      });
    },
  },
});

export const {
  setDriverStops,
  setSortMode,
  selectStop,
  clearSelectedStop,
  pickupStop,
  deliverStop,
  addDriverPhoto,
  updateDriverStop,
  mergeDriverStops
} = routeSlice.actions;

export default routeSlice.reducer;